import React, { useState, useRef, useEffect } from 'react';
import { usePlayer } from '@/contexts/PlayerContext';
import { GripVertical, Minimize2 } from 'lucide-react';
import btnPlay from "@/assets/btn-play.png";
import crowRight from "@/assets/crow-right.png";
import crowSkullLeft from "@/assets/crow-skull-left.png";
import musicNoteIcon from "@/assets/music-note-icon.png";
import { getDefaultCoverImage } from "@/utils/defaultCoverImage";

export default function GlobalPlayer() {
  const {
    currentTrack,
    isPlaying,
    togglePlayPause,
    skipToNext,
    skipToPrevious,
  } = usePlayer();

  const [position, setPosition] = useState({ x: 16, y: 120 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isMinimized, setIsMinimized] = useState(false);
  const [isDragModeActive, setIsDragModeActive] = useState(false); // Mobile drag mode
  const playerRef = useRef(null);
  const lastTapRef = useRef(0); // For double-tap detection
  const dragModeTimeoutRef = useRef(null); // Auto-disable drag mode

  // Mouse drag handling
  useEffect(() => {
    let rafId = null;
    let cachedDimensions = { width: 256, height: 100 };
    
    // Cache dimensions once when dragging starts to avoid forced reflows
    if (isDragging && playerRef.current) {
      cachedDimensions = {
        width: playerRef.current.offsetWidth,
        height: playerRef.current.offsetHeight
      };
    }
    
    const handleMouseMove = (e) => {
      if (!isDragging) return;
      
      // Use requestAnimationFrame to batch DOM reads/writes and avoid forced reflows
      if (rafId) cancelAnimationFrame(rafId);
      
      rafId = requestAnimationFrame(() => {
        const newX = e.clientX - dragOffset.x;
        const newY = e.clientY - dragOffset.y;
        
        // Constrain to viewport using cached dimensions
        const maxX = window.innerWidth - cachedDimensions.width;
        const maxY = window.innerHeight - cachedDimensions.height;
        
        setPosition({
          x: Math.max(0, Math.min(newX, maxX)),
          y: Math.max(0, Math.min(newY, maxY))
        });
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      if (rafId) cancelAnimationFrame(rafId);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove, { passive: true });
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [isDragging, dragOffset]);

  // Touch drag handling for mobile
  useEffect(() => {
    if (!isDragModeActive || !playerRef.current) return;
    
    let rafId = null;
    const cachedDimensions = {
      width: playerRef.current.offsetWidth,
      height: playerRef.current.offsetHeight
    };

    const handleTouchMove = (e) => {
      if (!isDragModeActive) return;
      
      const touch = e.touches[0];
      if (!touch) return;
      
      e.preventDefault(); // Prevent scrolling while dragging
      
      if (rafId) cancelAnimationFrame(rafId);
      
      rafId = requestAnimationFrame(() => {
        const newX = touch.clientX - dragOffset.x;
        const newY = touch.clientY - dragOffset.y;
        
        const maxX = window.innerWidth - cachedDimensions.width;
        const maxY = window.innerHeight - cachedDimensions.height;
        
        setPosition({
          x: Math.max(0, Math.min(newX, maxX)),
          y: Math.max(0, Math.min(newY, maxY))
        });
      });
    };

    const handleTouchEnd = () => {
      if (rafId) cancelAnimationFrame(rafId);
      // Keep drag mode active until timeout or another double-tap
    };

    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd);

    return () => {
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [isDragModeActive, dragOffset]);

  // Auto-disable drag mode after 5 seconds of inactivity
  useEffect(() => {
    if (isDragModeActive) {
      if (dragModeTimeoutRef.current) {
        clearTimeout(dragModeTimeoutRef.current);
      }
      dragModeTimeoutRef.current = setTimeout(() => {
        setIsDragModeActive(false);
      }, 5000);
    }
    
    return () => {
      if (dragModeTimeoutRef.current) {
        clearTimeout(dragModeTimeoutRef.current);
      }
    };
  }, [isDragModeActive, position]); // Reset timeout when position changes

  const handleMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    setDragOffset({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
    setIsDragging(true);
  };

  // Double-tap detection for mobile drag mode
  const handleTouchStart = (e) => {
    const touch = e.touches[0];
    if (!touch) return;
    
    const now = Date.now();
    const timeSinceLastTap = now - lastTapRef.current;
    
    if (timeSinceLastTap < 300 && timeSinceLastTap > 0) {
      // Double-tap detected - toggle drag mode
      e.preventDefault();
      e.stopPropagation();
      setIsDragModeActive(prev => !prev);
    }
    
    lastTapRef.current = now;
    
    // If drag mode is active, start tracking for drag
    if (isDragModeActive) {
      e.preventDefault();
      setDragOffset({
        x: touch.clientX - position.x,
        y: touch.clientY - position.y
      });
    }
  };


  if (!currentTrack) return null;

  return (
    <>

      {/* Minimized floating button */}
      {isMinimized && (
        <button
          onClick={() => setIsMinimized(false)}
          className="fixed bottom-24 md:bottom-36 right-2 md:right-4 z-[10000] neuro-base rounded-full p-2 md:p-3 backdrop-blur-sm hover:scale-110 transition-transform"
          title="Show player"
        >
          <img src={musicNoteIcon} alt="Music" className="w-6 h-6 md:w-8 md:h-8" loading="lazy" decoding="async" width="32" height="32" />
          {isPlaying && (
            <span className="absolute top-0 right-0 w-2 h-2 md:w-3 md:h-3 bg-green-500 rounded-full animate-pulse"></span>
          )}
        </button>
      )}

      {/* Full player */}
      {!isMinimized && (
        <div 
          ref={playerRef}
          onTouchStart={handleTouchStart}
          style={{
            left: `${position.x}px`,
            top: `${position.y}px`,
          }}
          className={`fixed z-[10000] neuro-base rounded-2xl p-2 md:p-3 w-[280px] md:w-80 backdrop-blur-sm relative ${
            isDragModeActive ? 'ring-2 ring-primary/50 ring-offset-2 ring-offset-background' : ''
          }`}
        >
        {/* Drag mode indicator for mobile */}
        {isDragModeActive && (
          <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-primary/80 text-primary-foreground text-xs px-2 py-1 rounded-full whitespace-nowrap">
            Drag mode • tap to move
          </div>
        )}
        
        {/* Drag Handle - Left Side, Full Height */}
        <div 
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
          className={`absolute left-0 top-0 bottom-0 w-10 flex flex-col items-center justify-center gap-2 rounded-l-2xl select-none ${
            isDragging || isDragModeActive ? 'cursor-grabbing bg-muted/30' : 'cursor-grab hover:bg-muted/20'
          } transition-colors`}
        >
          <GripVertical className="w-4 h-4 text-[#808080] pointer-events-none" />
          <GripVertical className="w-4 h-4 text-[#808080] pointer-events-none" />
          <GripVertical className="w-4 h-4 text-[#808080] pointer-events-none" />
          <GripVertical className="w-4 h-4 text-[#808080] pointer-events-none" />
        </div>
        
        <div className="flex items-start gap-2 pl-8 select-none">
          <button
            onClick={() => setIsMinimized(true)}
            onMouseDown={(e) => e.stopPropagation()}
            className="p-1 hover:bg-muted/50 rounded"
            title="Minimize player"
          >
            <Minimize2 className="w-4 h-4 text-[#808080]" />
          </button>
          

          <div className="flex-1 min-w-0 flex flex-col items-center">
            {/* Track Info */}
            <p className="text-sm text-[#d0d0d0] truncate font-medium text-center w-full">
              {currentTrack.title}
            </p>
            <p className="text-xs text-[#808080] truncate mb-3 text-center w-full">
              {currentTrack.artist_name}
            </p>

            {/* Controls */}
            <div className="flex items-center justify-center gap-2 md:gap-4">
              <div className="flex flex-col items-center">
                <button
                  onClick={skipToPrevious}
                  onMouseDown={(e) => e.stopPropagation()}
                  className="hover:scale-110 transition-transform rounded-lg p-0 flex items-center justify-center"
                >
                  <img src={crowSkullLeft} alt="Previous" className="w-10 h-10 md:w-12 md:h-12 object-contain" loading="lazy" decoding="async" width="48" height="48" />
                </button>
                <span className="text-[10px] md:text-xs text-[#808080] mt-0.5">back</span>
              </div>

              <div className="flex flex-col items-center">
                <button
                  onClick={togglePlayPause}
                  onMouseDown={(e) => e.stopPropagation()}
                  className="hover:scale-110 transition-transform rounded-lg p-0 flex items-center justify-center"
                >
                  <img src={btnPlay} alt={isPlaying ? "Pause" : "Play"} className="w-10 h-10 md:w-12 md:h-12 object-contain" loading="lazy" decoding="async" width="48" height="48" />
                </button>
              </div>

              <div className="flex flex-col items-center">
                <button
                  onClick={skipToNext}
                  onMouseDown={(e) => e.stopPropagation()}
                  className="hover:scale-110 transition-transform rounded-lg p-0 flex items-center justify-center"
                >
                  <img src={crowRight} alt="Next" className="w-10 h-10 md:w-12 md:h-12 object-contain" loading="lazy" decoding="async" width="48" height="48" />
                </button>
                <span className="text-[10px] md:text-xs text-[#808080] mt-0.5">skip</span>
              </div>
            </div>
          </div>
        </div>
        </div>
      )}
    </>
  );
}
